<template>
  <div class="pm-method" :class="{ 'pm-method_backup': backup }">
    <card-brand-badge :brand="method.cardBrand" />

    <div class="pm-method__info">
      <div class="pm-method__number">
        {{ maskedNumber }}
      </div>
      <div v-if="expiry" class="pm-method__expiry">
        Действует до {{ expiry }}
      </div>
    </div>

    <template v-if="backup">
      <div v-if="canUseBilling" class="pm-method__promote">
        <a
          class="pm-method__promote-link"
          :class="{ 'pm-method__promote-link_disabled': busy }"
          @click="$emit('set-default')"
        >Сделать основной</a>
        <div class="pm-method__promote-hint">
          Сделать основной для списания средств
        </div>
      </div>

      <!-- держит место переключателя основной карты, иначе текст съезжает вправо -->
      <div class="pm-method__control" />

      <v-tooltip v-if="canUseBilling" bottom>
        <template #activator="{ on, attrs }">
          <v-btn
            icon
            :ripple="false"
            class="pm-method__delete"
            :disabled="busy"
            v-bind="attrs"
            v-on="on"
            @click="$emit('delete')"
          >
            <v-icon color="#EB5757">
              mdi-trash-can-outline
            </v-icon>
          </v-btn>
        </template>
        <span>Удалить метод оплаты</span>
      </v-tooltip>
    </template>

    <template v-else>
      <div class="pm-method__autopay">
        <div class="pm-method__autopay-label">
          Автоплатеж
        </div>
        <div class="pm-method__autopay-hint">
          Ежемесячное списание средств согласно тарифу, удобно!
        </div>
      </div>

      <!-- Без права на управление переключатель показывает состояние, но не меняет его -->
      <v-switch
        :input-value="method.autopayEnabled"
        :loading="autopayBusy"
        :disabled="autopayBusy || !canUseBilling"
        color="#FF9100"
        hide-details
        dense
        inset
        class="pm-method__switch mt-0 pt-0"
        @change="$emit('toggle-autopay', $event)"
      />
    </template>
  </div>
</template>

<script>
  import CardBrandBadge from '@/components/billing/CardBrandBadge.vue'

  export default {
    name: 'PaymentMethodCard',
    components: { CardBrandBadge },
    props: {
      method: {
        type: Object,
        required: true,
      },
      // Резервная карта: половина ряда, «Сделать основной» и удаление вместо автоплатежа.
      backup: {
        type: Boolean,
        default: false,
      },
      busy: {
        type: Boolean,
        default: false,
      },
      autopayBusy: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      maskedNumber () {
        return this.method.last4
          ? `•••• •••• •••• ${this.method.last4}`
          : '•••• •••• •••• ••••'
      },
      expiry () {
        if (!this.method.expMonth || !this.method.expYear) return null
        const mm = String(this.method.expMonth).padStart(2, '0')
        const yy = String(this.method.expYear).slice(-2)
        return `${mm} / ${yy}`
      },
    },
  }
</script>

<style lang="less" scoped>
.pm-method {
  display: flex;
  align-items: center;
  gap: 20px;
  // ширину даёт колонка сетки .pm-row
  min-width: 0;
  background-color: #f1f3f6;
  border-radius: 6px;
  padding: 20px 24px;

  // на двух колонках карточке достаётся ~530px — отступы отдаём подписи автоплатежа
  .media-small-pc({
    gap: 16px;
    padding: 20px;
  });

  // ниже ~560px строка не вмещает бейдж, номер, подпись и переключатель —
  // содержимое переносится в два ряда
  .media-xs({
    flex-wrap: wrap;
    gap: 12px 16px;
    padding: 16px;
  });

  &_backup {
    position: relative;

    &:hover .pm-method__delete,
    &:focus-within .pm-method__delete {
      opacity: 1;
      pointer-events: auto;
    }
  }

  &__info {
    min-width: 210px;

    .media-xs({
      flex: 1 1 auto;
      min-width: 0;
    });
  }

  &__number {
    font-size: 18px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.87);
    letter-spacing: 1px;

    // на телефоне 18px + разрядка не оставляют места бейджу
    .media-xs({
      font-size: 16px;
      letter-spacing: 0.5px;
    });
  }

  &__expiry {
    margin-top: 4px;
    font-size: 14px;
    color: rgba(0, 0, 0, 0.54);
  }

  &__autopay {
    flex: 1 1 auto;
    text-align: right;

    // второй ряд: подпись и переключатель рядом. Базис с запасом — при точном
    // «100% минус переключатель» тот срывается на третью строку
    .media-xs({
      flex: 1 1 60%;
      text-align: left;
    });

    &-label {
      font-size: 16px;
      font-weight: 500;
      color: rgba(0, 0, 0, 0.87);
    }

    &-hint {
      margin-top: 4px;
      font-size: 13px;
      color: rgba(0, 0, 0, 0.54);
    }
  }

  // Правая колонка одной ширины у обеих карт: у основной в ней переключатель,
  // у резервной — пусто, но текст встаёт по той же вертикали.
  // 46px — ширина v-switch: 38px поля + 8px его правого отступа.
  &__switch,
  &__control {
    flex: 0 0 46px;
  }

  // место переключателя нужно только пока кнопка удаления вне потока
  &__control {
    @media (max-width: @xs), (hover: none) {
      display: none;
    }
  }

  &__promote {
    flex: 1 1 auto;
    text-align: right;

    .media-xs({
      flex: 1 1 60%;
      text-align: left;
    });

    &-link {
      // блок по ширине текста и прижат вправо: иначе перенос строки в разметке
      // добавляет пробел справа и правый край уезжает от подписи
      display: block;
      width: fit-content;
      margin-left: auto;
      font-size: 16px;
      color: #828282;
      text-decoration: underline;
      cursor: pointer;

      .media-xs({
        margin-left: 0;
      });

      &:hover {
        color: #5a5a5a;
      }

      &_disabled {
        pointer-events: none;
        opacity: 0.5;
      }
    }

    &-hint {
      margin-top: 4px;
      font-size: 13px;
      color: rgba(0, 0, 0, 0.54);
    }
  }

  &__delete.v-btn {
    position: absolute;
    right: 8px;
    bottom: 8px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease;

    // на тач-экранах hover не наступает — кнопку прятать нельзя;
    // она встаёт в поток на место переключателя основной карты. Ширину бокса
    // держим по кнопке: растянутый flex-basis превращает круглую подсветку в овал
    @media (max-width: @xs), (hover: none) {
      // именно relative: при static оверлей кнопки (::before, 100% × 100%)
      // считает размеры от карточки и растекается овалом на всю её площадь
      position: relative;
      right: auto;
      bottom: auto;
      flex: 0 0 36px;
      margin-right: 10px;
      opacity: 1;
      pointer-events: auto;
    }
  }
}
</style>
